import { useTranslation } from "react-i18next";
import { Database } from "lucide-react";

import { FieldCard } from "@/components/brand/SettingsShell";
import { Pill } from "@/components/brand/Pill";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ProjectSourceFields,
  type ProjectSourceDraft,
} from "@/components/projects/ProjectSourceFields";
import { IDES_DEMO_PACKAGES, parseDemoPackages } from "@/lib/demo-packages";
import { WINDOW_DEFAULT_MONTHS } from "@/lib/projectSource";
import type { Project, UpdateProjectRequest } from "@/types/api";

/**
 * O rascunho das configurações: origem, modo e janela (#1784) e os pacotes de demonstração da SAP
 * (#1785), que moravam na conexão de origem. A caixa não é gravada: o projeto é de demonstração
 * quando declara pacotes.
 */
export interface SettingsSourceDraft extends ProjectSourceDraft {
  demo_enabled: boolean;
  demo_text: string;
}

/** O que o projeto tem hoje, no formato do formulário. */
export function sourceDraftOf(project: Project): SettingsSourceDraft {
  const demo = project.demo_packages ?? [];
  return {
    source_type: project.source_type ?? "",
    mode: project.mode ?? "",
    window_months: String(project.window_months ?? WINDOW_DEFAULT_MONTHS),
    demo_enabled: demo.length > 0,
    demo_text: demo.join("\n"),
  };
}

/** Os pacotes do rascunho; com a caixa desmarcada, nenhum. */
export function draftDemoPackages(draft: SettingsSourceDraft) {
  return parseDemoPackages(draft.demo_enabled ? draft.demo_text : "");
}

/** Só o que mudou vai no PATCH: reenviar a origem igual não é mudança, e não esbarra na trava. */
export function changedSourceFields(
  project: Project,
  draft: SettingsSourceDraft,
): UpdateProjectRequest {
  const body: UpdateProjectRequest = {};
  if (draft.source_type && draft.source_type !== project.source_type) {
    body.source_type = draft.source_type;
  }
  if (draft.mode && draft.mode !== project.mode) body.mode = draft.mode;
  // Resposta sem a janela (de antes da #1784) vale como o padrão, que é o que o banco preencheu.
  const months = Number(draft.window_months);
  if (months !== (project.window_months ?? WINDOW_DEFAULT_MONTHS)) body.window_months = months;
  const demo = draftDemoPackages(draft).packages;
  if (demo.join("\n") !== (project.demo_packages ?? []).join("\n")) body.demo_packages = demo;
  return body;
}

/**
 * Origem, modo, janela e pacotes de demonstração do projeto nas configurações (#1784, #1785).
 *
 * Só mostra: o rascunho e o salvar são os da página, no par único do rodapé (#1042). A recusa da
 * api (409, origem e modo travados pela primeira carga do ex-kit) chega pelo toast do salvar.
 */
export function ProjectSourceCard({
  id,
  value,
  onChange,
  dirty,
  windowInvalid,
}: {
  id: string;
  value: SettingsSourceDraft;
  onChange: (next: Partial<SettingsSourceDraft>) => void;
  dirty: boolean;
  windowInvalid: boolean;
}) {
  const { t } = useTranslation();
  const demo = draftDemoPackages(value);
  // Marcar carrega a curadoria do IDES; desmarcar limpa (#1072).
  const toggleDemo = (checked: boolean) =>
    onChange({ demo_enabled: checked, demo_text: checked ? IDES_DEMO_PACKAGES.join("\n") : "" });
  return (
    <FieldCard
      id={id}
      icon={<Database />}
      title={t("Source and extraction")}
      sub={t("What the extraction kit reads from this client, and how far back.")}
      pill={dirty ? <Pill tone="warn">{t("Unsaved changes")}</Pill> : undefined}
    >
      <ProjectSourceFields idPrefix="settings" value={value} onChange={onChange} />
      {windowInvalid && (
        <p className="text-[11px] text-[#b02a31] mt-1.5">
          {t("The analysis window goes from 1 to 240 months.")}
        </p>
      )}
      {/* #1072: no IDES quase todo objeto Z tem autor SAP, e a regra de autoria tirava tudo da
          conta. Projeto de cliente deixa a caixa desmarcada. */}
      <div className="mt-4 rounded-md border border-border bg-muted/30">
        <div className="flex items-center gap-3 px-3 py-3">
          <Checkbox
            id="settings-demo-data"
            className="shrink-0"
            checked={value.demo_enabled}
            onCheckedChange={(v) => toggleDemo(v === true)}
          />
          <div className="space-y-1">
            <Label htmlFor="settings-demo-data" className="cursor-pointer leading-none">
              {t("Use SAP demo data")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("Only for a demo system such as the IDES. Leave unchecked for a customer system.")}
            </p>
          </div>
        </div>
        {value.demo_enabled && (
          <div className="space-y-2 border-t border-border px-3 py-3">
            <Label htmlFor="settings-demo-packages">{t("SAP demo packages")}</Label>
            <Textarea
              id="settings-demo-packages"
              rows={4}
              className="bg-background font-mono text-xs"
              placeholder={"Z_ADMSTFHR\nZHCM_TRANSLATION"}
              value={value.demo_text}
              onChange={(e) => onChange({ demo_text: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              {t("Comes loaded with the packages curated for the IDES. Objects in these packages enter the Architect even with SAP as author, marked as demo content.")}
            </p>
            {demo.invalid.length > 0 && (
              <p className="text-xs text-destructive">
                {t("Outside the customer namespace (Z, Y or /): {{list}}", {
                  list: demo.invalid.join(", "),
                })}
              </p>
            )}
          </div>
        )}
      </div>
    </FieldCard>
  );
}
